import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Select, TextInput } from "flowbite-react";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { HiPencilAlt } from "react-icons/hi";
import type { ContractType } from "../../services/contract-types";
import { updateContractType } from "../../services/contract-types";
import type { ContractCompany } from "../../services/contract-companies";
import { getContractCompanies } from "../../services/contract-companies";
import type { ContractCategory } from "../../services/contract-categories";
import { getContractCategories } from "../../services/contract-categories";

interface EditContractTypeModalProps {
  contractType: ContractType;
  onUpdate: () => Promise<void>;
}

export const EditContractTypeModal: FC<EditContractTypeModalProps> = function ({
  contractType,
  onUpdate,
}) {
  const [isOpen, setOpen] = useState(false);
  const [isActive, setIsActive] = useState(contractType.active);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companies, setCompanies] = useState<ContractCompany[]>([]);
  const [categories, setCategories] = useState<ContractCategory[]>([]);
  const [formData, setFormData] = useState({
    type: contractType.type,
    company: contractType.company,
    category: contractType.category,
    description: contractType.description || "",
    min_duration_months: contractType.min_duration_months || 24,
  });

  useEffect(() => {
    const loadData = async () => {
      const [companiesData, categoriesData] = await Promise.all([
        getContractCompanies(),
        getContractCategories(),
      ]);
      setCompanies(companiesData);
      setCategories(categoriesData);
    };
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!formData.type || !formData.company || !formData.category) {
      console.error("Missing required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await updateContractType(contractType.id, {
        ...formData,
        active: isActive,
      });

      if (result.success) {
        await onUpdate();
        setOpen(false);
      } else {
        console.error("Failed to update contract type:", result.error);
      }
    } catch (error) {
      console.error("Error updating contract type:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button color="primary" size="sm" onClick={() => setOpen(!isOpen)}>
        <HiPencilAlt className="mr-2 text-lg" />
        Bearbeiten
      </Button>
      <Modal onClose={() => setOpen(false)} show={isOpen}>
        <ModalHeader className="border-b border-gray-200 !p-6 dark:border-gray-700">
          <strong>Vertragstyp bearbeiten</strong>
        </ModalHeader>
        <ModalBody>
          <form>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <label htmlFor="type">Typname</label>
                <TextInput
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <label htmlFor="company">Unternehmen</label>
                <Select
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={(e) =>
                    setFormData({ ...formData, company: e.target.value })
                  }
                  className="mt-1"
                >
                  <option value="">Unternehmen auswählen</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.company}>
                      {company.company}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label htmlFor="category">Kategorie</label>
                <Select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="mt-1"
                >
                  <option value="">Kategorie auswählen</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.category}>
                      {category.category}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label htmlFor="min_duration_months">
                  Mindestlaufzeit (Monate)
                </label>
                <TextInput
                  id="min_duration_months"
                  name="min_duration_months"
                  type="number"
                  value={formData.min_duration_months}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      min_duration_months: parseInt(e.target.value),
                    })
                  }
                  className="mt-1"
                />
              </div>
              <div className="lg:col-span-2">
                <label htmlFor="description">Beschreibung</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={4}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                />
              </div>
              <div className="lg:col-span-2">
                <div className="flex items-center gap-2">
                  <input
                    id="active"
                    type="checkbox"
                    checked={isActive}
                    onChange={() => setIsActive(!isActive)}
                    className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
                  />
                  <label htmlFor="active">Aktiver Status</label>
                </div>
              </div>
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          <div className="flex gap-2">
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Wird gespeichert..." : "Änderungen speichern"}
            </Button>
            <Button color="gray" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};
